import React, { useState } from "react";
import { observer } from "mobx-react";
import { Button, Modal, Stack, TextInput, Text, Alert } from "@mantine/core";

import { Cubby } from "../../stores/Cubby";
import { MODALS } from "../../helpers/constants";
import { TextWithInfoAndIcon } from "../dataDisplay/textWithInfoAndIcon";
import { sendToRailsController } from "../../helpers/requests";
import { IconAt, IconFileInvoice, IconInfoCircle } from "@tabler/icons-react";
import { ARTICLES } from "../../docs/docsIndex";

const PayToLightningAddress = observer(({ cubby }: { cubby: Cubby }) => {
  const [
    proposedLightningAddressForPayment,
    setProposedLightningAddressForPayment,
  ] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const shouldShowPayToInvoiceSuggestion = () => {
    return (
      proposedLightningAddressForPayment.length > 30 &&
      !proposedLightningAddressForPayment.includes("@") &&
      proposedLightningAddressForPayment.startsWith("lnbc")
    );
  };

  const handleStartPayment = () => {
    const cleanedAddress = proposedLightningAddressForPayment.trim();
    if (!validateEmail(cleanedAddress)) {
      setError("A valid lightning address looks like an email address.");
      return;
    }

    setLoading(true);
    sendToRailsController(
      {
        proposedLightningAddressForPayment: cleanedAddress,
        cubbyUuid: cubby.cubbyUuid,
      },
      "/cubby_life/get_lightning_address_info"
    )
      .then((response) => {
        console.log("response from pay_to_lightning_address", response);
        if (response.error) {
          setError(response.error);
        } else {
          console.log("this is the response to use for the payment", response);
          const lightningAddressData = response.lightningAddressData;
          lightningAddressData.paymentAddress = cleanedAddress;
          cubby.setStagedLightningAddressDataForPayment(lightningAddressData);
          setProposedLightningAddressForPayment("");
          cubby.setModal(MODALS.CONFIRM_PAY_TO_LIGHTNING_ADDRESS);
        }
      })
      .catch(() => {
        setError("An error occurred. Please try again.");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Modal
      opened={cubby.currentModal === MODALS.PAY_TO_LIGHTNING_ADDRESS}
      onClose={() => {
        setProposedLightningAddressForPayment("");
        cubby.setModal(null);
      }}
      title={
        <TextWithInfoAndIcon
          text="Pay To Lightning Address"
          articleSlug={ARTICLES.SENDING_TO_LIGHTING_ADDRESS.slug}
          icon={<IconAt size={24} />}
          cubby={cubby}
          textSize="lg"
        />
      }
    >
      <Stack>
        <TextInput
          placeholder="Enter Lightning address (e.g., user@example.com)"
          value={proposedLightningAddressForPayment}
          onChange={(event) => {
            setProposedLightningAddressForPayment(event.currentTarget.value);
            setError("");
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              handleStartPayment();
            }
          }}
          error={error}
          maxLength={50}
          disabled={loading}
        />

        {shouldShowPayToInvoiceSuggestion() && (
          <Stack>
            <Alert
              variant="light"
              color="blue"
              title="Are you trying to pay an invoice?"
              icon={<IconInfoCircle />}
            >
              What you've entered looks like a Lightning invoice.
            </Alert>
            <Button
              leftSection={<IconFileInvoice />}
              size="sm"
              variant="default"
              aria-label="pay to invoice"
              onClick={() => {
                setProposedLightningAddressForPayment("");
                cubby.setModal(MODALS.SEND_VIA_BOLT_11_INVOICE);
              }}
            >
              Pay To Invoice
            </Button>
          </Stack>
        )}

        <Button
          onClick={handleStartPayment}
          disabled={loading || shouldShowPayToInvoiceSuggestion()}
        >
          Start Payment
        </Button>
      </Stack>
    </Modal>
  );
});

export default PayToLightningAddress;
